import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Shield,
  Users,
  Church,
  Heart,
  Loader2,
  Search,
  X,
  MoreVertical,
  Trash2,
  Pencil,
  Key,
  Crown,
  UserCircle,
  MapPin,
  Plus,
  BarChart3,
  Clock,
  ShieldOff,
  ShieldCheck
} from "lucide-react";
import { apiClient } from "../lib/apiClient";
import { HelpDialog } from "../components/HelpDialog";
import { AdminNav } from "../components/AdminNav";
import { AdminStatsOverview } from "../components/AdminStatsOverview";


export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [churches, setChurches] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [helpOpen, setHelpOpen] = useState(false);

  // Search states
  const [userSearch, setUserSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [churchSearch, setChurchSearch] = useState("");

  // Dialog states
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ name: "", church_id: "", is_admin: false });
  // "Manage admins" dialog: open state + which church + search filter
  const [manageAdminsChurch, setManageAdminsChurch] = useState(null);
  const [manageAdminsSearch, setManageAdminsSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwForm, setResetPwForm] = useState({ password: "" });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'user'|'group'|'church', id, name }

  const [churchFormOpen, setChurchFormOpen] = useState(false);
  const [churchForm, setChurchForm] = useState({ name: "", address: "" });
  const [editingChurch, setEditingChurch] = useState(null);

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignGroup, setReassignGroup] = useState(null);
  const [reassignOwnerId, setReassignOwnerId] = useState("");

  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editGroupForm, setEditGroupForm] = useState({ name: "", description: "", church_id: "" });
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [moveMembersOpen, setMoveMembersOpen] = useState(false);
  const [moveMembersGroup, setMoveMembersGroup] = useState(null);
  const [moveMembersTarget, setMoveMembersTarget] = useState("");
  const [moveMembersSelected, setMoveMembersSelected] = useState([]);
  const [moveMembersData, setMoveMembersData] = useState([]);

  const handleLogout = useCallback(async () => {
    try { await apiClient.post("authLogout"); } catch { /* ignore logout errors */ }
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    navigate("/login");
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      // Church Admins don't have access to /admin/* endpoints — fetch what they can.
      const userData = localStorage.getItem("user");
      const u = userData ? JSON.parse(userData) : null;
      if (u?.is_admin) {
        const [statsRes, usersRes, churchesRes, groupsRes] = await Promise.all([
          apiClient.get("adminStats"),
          apiClient.get("adminUsers"),
          apiClient.get("churches"),
          apiClient.get("adminGroups")
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
        setChurches(churchesRes.data);
        setGroups(groupsRes.data);
      } else {
        const churchesRes = await apiClient.get("churches");
        setChurches(churchesRes.data);
      }
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else if (err.response?.status === 403) navigate("/dashboard");
      else alert("Failed to load admin data. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [handleLogout, navigate]);  

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) { navigate("/login"); return; }
    const u = JSON.parse(userData);
    // Allow global admins AND church admins (the latter scoped to their church[es])
    const isChurchAdmin = Array.isArray(u.church_admin_ids) && u.church_admin_ids.length > 0;
    if (!u.is_admin && !isChurchAdmin) { navigate("/dashboard"); return; }
    setUser(u);
    // Church admins default to the Churches tab since Overview/Users/Groups aren't visible to them.
    if (!u.is_admin) setActiveTab("churches");
    fetchData();
  }, [navigate, fetchData]);

  // === User actions ===
  const openEditUser = (u) => {
    setSelectedUser(u);
    setEditUserForm({ name: u.name, church_id: u.church_id || "", is_admin: u.is_admin || false });
    setEditUserOpen(true);
  };

  const submitEditUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {};
      if (editUserForm.name !== selectedUser.name) payload.name = editUserForm.name;
      const newChurch = editUserForm.church_id || null;
      if (newChurch !== (selectedUser.church_id || null)) payload.church_id = newChurch;
      if (editUserForm.is_admin !== (selectedUser.is_admin || false)) payload.is_admin = editUserForm.is_admin;
      if (Object.keys(payload).length > 0) {
        await apiClient.patch("adminUser", selectedUser.id, payload);
      }
      setEditUserOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update user");
    } finally {
      setFormLoading(false);
    }
  };

  const toggleChurchAdmin = async (userId, churchId, grant) => {
    try {
      await apiClient.patch("adminUserChurchAdmin", userId, { church_id: churchId, grant });
      // Optimistic: update the selectedUser in place so the dialog reflects immediately
      setSelectedUser((prev) => {
        if (!prev || prev.id !== userId) return prev;
        const existing = prev.church_admin_ids || [];
        const next = grant ? [...new Set([...existing, churchId])] : existing.filter((id) => id !== churchId);
        return { ...prev, church_admin_ids: next };
      });
      // Refresh the user list so row badges also update
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update Church Admin status");
    }
  };

  const openManageAdmins = (church) => {
    setManageAdminsChurch(church);
    setManageAdminsSearch("");
  };

  const openResetPw = (u) => {
    setSelectedUser(u);
    setResetPwForm({ password: "" });
    setResetPwOpen(true);
  };

  const submitResetPw = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await apiClient.post("adminUserResetPassword", selectedUser.id, { password: resetPwForm.password });
      setResetPwOpen(false);
      alert(`Password reset for ${selectedUser.name}`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setFormLoading(false);
    }
  };

  // === Delete actions ===
  const openDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setDeleteConfirmOpen(true);
  };

  const submitDelete = async () => {
    setFormLoading(true);
    try {
      const { type, id } = deleteTarget;
      if (type === "user") await apiClient.delete("adminUser", id);
      else if (type === "group") await apiClient.delete("adminGroup", id);
      else if (type === "church") await apiClient.delete("church", id);
      setDeleteConfirmOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    } finally {
      setFormLoading(false);
    }
  };

  // === Church actions ===
  const openChurchForm = (church = null) => {
    setEditingChurch(church);
    setChurchForm(church ? { name: church.name, address: church.address || "" } : { name: "", address: "" });
    setChurchFormOpen(true);
  };

  const submitChurchForm = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { name: churchForm.name, address: churchForm.address || null };
      if (editingChurch) {
        await apiClient.patch("church", editingChurch.id, payload);
      } else {
        await apiClient.post("churches", payload);
      }
      setChurchFormOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save church");
    } finally {
      setFormLoading(false);
    }
  };

  // === Group actions ===
  const openReassign = (group) => {
    setReassignGroup(group);
    setReassignOwnerId("");
    setReassignOpen(true);
  };

  const submitReassign = async (e) => {
    e.preventDefault();
    if (!reassignOwnerId) return;
    setFormLoading(true);
    try {
      await apiClient.patch("adminGroupOwner", reassignGroup.id, { owner_id: reassignOwnerId });
      setReassignOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reassign");
    } finally {
      setFormLoading(false);
    }
  };

  // === Group edit actions ===
  const openEditGroup = (g) => {
    setSelectedGroup(g);
    setEditGroupForm({ name: g.name, description: g.description || "", church_id: g.church_id || "" });
    setEditGroupOpen(true);
  };

  const submitEditGroup = async (e) => {
    e.preventDefault();
    if (!selectedGroup) return;
    setFormLoading(true);
    try {
      const payload = { name: editGroupForm.name };
      if (editGroupForm.description !== (selectedGroup.description || "")) {
        payload.description = editGroupForm.description.trim() || null;
      }
      const newChurchId = editGroupForm.church_id || null;
      if (newChurchId !== (selectedGroup.church_id || null)) {
        payload.church_id = newChurchId;
      }
      await apiClient.patch("group", selectedGroup.id, payload);
      setEditGroupOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update group");
    } finally {
      setFormLoading(false);
    }
  };

  const toggleGroupStatus = async (group) => {
    try {
      await apiClient.patch("adminGroupToggleStatus", group.id);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update group status");
    }
  };

  const openMoveMembers = async (group) => {
    setMoveMembersGroup(group);
    setMoveMembersTarget("");
    setMoveMembersSelected([]);
    try {
      const res = await apiClient.get("groupMembers", group.id);
      setMoveMembersData(res.data);
    } catch {
      setMoveMembersData([]);
    }
    setMoveMembersOpen(true);
  };

  const toggleMoveMember = (memberId) => {
    setMoveMembersSelected((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const submitMoveMembers = async () => {
    if (!moveMembersTarget || moveMembersSelected.length === 0) return;
    setFormLoading(true);
    try {
      await apiClient.post("adminGroupMoveMembers", moveMembersGroup.id, {
        target_group_id: moveMembersTarget,
        member_ids: moveMembersSelected
      });
      setMoveMembersOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to move members");
    } finally {
      setFormLoading(false);
    }
  };

  // Scope (permission gating)
  const isGlobalAdmin = !!user?.is_admin;
  const managedChurchIds = user?.church_admin_ids || [];
  // For church-admins, only show churches they manage.
  const scopedChurches = isGlobalAdmin ? churches : churches.filter(c => managedChurchIds.includes(c.id));

  // Filters
  const filteredUsers = users.filter(u =>
    !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );
  const filteredGroups = groups.filter(g =>
    !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase())
  );
  const filteredChurches = scopedChurches.filter(c =>
    !churchSearch || c.name.toLowerCase().includes(churchSearch.toLowerCase()) || (c.address && c.address.toLowerCase().includes(churchSearch.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      <AdminNav user={user} onHelp={() => setHelpOpen(true)} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-stone w-full overflow-x-auto flex" data-testid="admin-tabs">
            {isGlobalAdmin && (
              <TabsTrigger value="overview" className="data-[state=active]:bg-sage data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <BarChart3 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
            )}
            {isGlobalAdmin && (
              <TabsTrigger value="users" className="data-[state=active]:bg-sage data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <Users className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Users </span>({users.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="churches" className="data-[state=active]:bg-sage data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Church className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Churches </span>({scopedChurches.length})
            </TabsTrigger>
            {isGlobalAdmin && (
              <TabsTrigger value="groups" className="data-[state=active]:bg-sage data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <Heart className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Groups </span>({groups.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <AdminStatsOverview stats={stats} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl font-semibold text-stone-dark" data-testid="admin-users-title">User Management</h2>
            </div>
            <SearchBar value={userSearch} onChange={setUserSearch} placeholder="Search users..." testId="admin-search-users" />
            <div className="space-y-2 mt-4">
              {filteredUsers.length === 0 ? (
                <p className="text-stone-dark/50 text-center py-8">No users match your search</p>
              ) : filteredUsers.map((u) => (
                <Card key={u.id} className="border-stone" data-testid={`admin-user-${u.id}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${u.is_admin ? 'bg-purple-100' : 'bg-sage/10'}`}>
                        {u.is_admin ? <Shield className="w-4 h-4 text-purple-600" /> : <UserCircle className="w-4 h-4 text-sage" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm text-stone-dark truncate">{u.name}</p>
                          {u.is_admin && <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full shrink-0">ADMIN</span>}
                          {!u.is_admin && u.church_admin_ids?.length > 0 && (
                            <span className="text-[10px] font-semibold bg-sage/15 text-sage px-1.5 py-0.5 rounded-full shrink-0" data-testid={`user-church-admin-badge-${u.id}`}>
                              CHURCH ADMIN ({u.church_admin_ids.length})
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-dark/50 truncate">{u.email}</p>
                        {u.church_name && <p className="text-xs text-sage/70 truncate">{u.church_name}</p>}
                        <p className="text-xs text-stone-dark/40 flex items-center gap-1 mt-0.5" data-testid={`user-last-login-${u.id}`}>
                          <Clock className="w-3 h-3 shrink-0" />
                          {u.last_login ? `Last login: ${new Date(u.last_login).toLocaleDateString()}` : "Never logged in"}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`admin-user-actions-${u.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => openEditUser(u)}>
                          <Pencil className="w-4 h-4 mr-2" />Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openResetPw(u)}>
                          <Key className="w-4 h-4 mr-2" />Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {u.id !== user?.id && (
                          <DropdownMenuItem onClick={() => openDelete("user", u.id, u.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Delete User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Churches Tab */}
          <TabsContent value="churches">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-dark" data-testid="admin-churches-title">
                {isGlobalAdmin ? "Church Management" : "Your Churches"}
              </h2>
              {isGlobalAdmin && (
                <Button className="bg-sage hover:bg-sage-dark text-white rounded-full" onClick={() => openChurchForm()} data-testid="admin-add-church-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Church
                </Button>
              )}
            </div>
            <SearchBar value={churchSearch} onChange={setChurchSearch} placeholder="Search churches..." testId="admin-search-churches" />
            <div className="space-y-2 mt-4">
              {filteredChurches.length === 0 ? (
                <p className="text-stone-dark/50 text-center py-8">
                  {isGlobalAdmin ? "No churches match your search" : "You are not a Church Admin for any church yet. Ask a global admin to grant access."}
                </p>
              ) : filteredChurches.map((c) => {
                const admins = users.filter(u => u.church_admin_ids?.includes(c.id));
                return (
                  <Card key={c.id} className="border-stone" data-testid={`admin-church-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center shrink-0">
                            <Church className="w-5 h-5 text-sage" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-stone-dark truncate">{c.name}</p>
                            {c.address && <p className="text-xs text-stone-dark/50 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{c.address}</p>}
                            <p className="text-xs text-stone-dark/40 mt-0.5">{c.group_count} group{c.group_count !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openChurchForm(c)} data-testid={`admin-edit-church-${c.id}`}>
                            <Pencil className="w-4 h-4 text-stone-dark/50" />
                          </Button>
                          {isGlobalAdmin && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openDelete("church", c.id, c.name)} data-testid={`admin-delete-church-${c.id}`}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Church admins list */}
                      {isGlobalAdmin && (
                        <div className="pt-3 border-t border-stone/60 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-stone-dark/60 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5" /> Managed by
                            </p>
                            {admins.length === 0 ? (
                              <p className="text-xs text-stone-dark/40 italic">No church admins assigned</p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5" data-testid={`admin-church-admins-${c.id}`}>
                                {admins.map(a => (
                                  <span key={a.id} className="text-xs bg-sage/10 text-sage px-2 py-0.5 rounded-full">
                                    {a.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full border-sage/40 text-sage hover:bg-sage/10 shrink-0"
                            onClick={() => openManageAdmins(c)}
                            data-testid={`admin-manage-admins-${c.id}`}
                          >
                            <ShieldCheck className="w-4 h-4 mr-1" />
                            Manage admins
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl font-semibold text-stone-dark" data-testid="admin-groups-title">Group Management</h2>
            </div>
            <SearchBar value={groupSearch} onChange={setGroupSearch} placeholder="Search groups..." testId="admin-search-groups" />
            <div className="space-y-2 mt-4">
              {filteredGroups.length === 0 ? (
                <p className="text-stone-dark/50 text-center py-8">No groups match your search</p>
              ) : filteredGroups.map((g) => (
                <Card key={g.id} className={`border-stone ${g.is_disabled ? 'opacity-60' : ''}`} data-testid={`admin-group-${g.id}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${g.is_disabled ? 'bg-stone-dark/10' : 'bg-rose-50'}`}>
                        <Heart className={`w-4 h-4 ${g.is_disabled ? 'text-stone-dark/30' : 'text-rose-500'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-stone-dark truncate">{g.name}</p>
                          {g.is_disabled && (
                            <span className="text-[10px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`group-disabled-badge-${g.id}`}>DISABLED</span>
                          )}
                        </div>
                        <p className="text-xs text-stone-dark/50">
                          {g.member_count} member{g.member_count !== 1 ? "s" : ""} · Owner: {g.owner_name}
                        </p>
                        {g.church_name && <p className="text-xs text-sage/70 truncate">{g.church_name}</p>}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`admin-group-actions-${g.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => navigate(`/groups/${g.id}`)}>
                          <Heart className="w-4 h-4 mr-2" />View Group
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditGroup(g)}>
                          <Pencil className="w-4 h-4 mr-2" />Edit Group
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openReassign(g)}>
                          <Crown className="w-4 h-4 mr-2" />Reassign Owner
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleGroupStatus(g)} data-testid={`admin-toggle-group-${g.id}`}>
                          {g.is_disabled ? (
                            <><ShieldCheck className="w-4 h-4 mr-2 text-green-600" />Enable Group</>
                          ) : (
                            <><ShieldOff className="w-4 h-4 mr-2 text-amber-600" />Disable Group</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMoveMembers(g)} data-testid={`admin-move-members-${g.id}`}>
                          <Users className="w-4 h-4 mr-2 text-blue-600" />Move Members
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openDelete("group", g.id, g.name)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />Delete Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit User</DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEditUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} data-testid="admin-edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Church</Label>
              <Select value={editUserForm.church_id || "none"} onValueChange={(v) => setEditUserForm({ ...editUserForm, church_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="admin-edit-user-church">
                  <SelectValue placeholder="No church" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No church</SelectItem>
                  {churches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="adminToggle"
                checked={editUserForm.is_admin}
                onChange={(e) => setEditUserForm({ ...editUserForm, is_admin: e.target.checked })}
                className="rounded border-stone"
                disabled={selectedUser?.id === user?.id}
                data-testid="admin-edit-user-admin-toggle"
              />
              <Label htmlFor="adminToggle" className="text-sm font-normal cursor-pointer">Administrator</Label>
              {selectedUser?.id === user?.id && <span className="text-xs text-stone-dark/40">(cannot change own status)</span>}
            </div>

            {/* Church Admin assignments — only global admins can toggle */}
            {churches.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-stone">
                <Label className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-sage" />
                  Church Admin for…
                </Label>
                <p className="text-xs text-stone-dark/50">Grants edit access to the church and its groups.</p>
                <div className="max-h-36 overflow-y-auto border border-stone rounded-lg p-2 space-y-1" data-testid="admin-edit-user-church-admin-list">
                  {churches.map((c) => {
                    const isGranted = selectedUser?.church_admin_ids?.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-sage/5 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!isGranted}
                          onChange={(e) => toggleChurchAdmin(selectedUser.id, c.id, e.target.checked)}
                          className="rounded border-stone"
                          data-testid={`admin-edit-user-church-admin-${c.id}`}
                        />
                        <span className="text-sm text-stone-dark truncate">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-sage hover:bg-sage-dark text-white rounded-full" disabled={formLoading} data-testid="admin-save-user-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitResetPw} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={resetPwForm.password} onChange={(e) => setResetPwForm({ password: e.target.value })} placeholder="Min 6 characters" minLength={6} required data-testid="admin-reset-pw-input" />
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-sage hover:bg-sage-dark text-white rounded-full" disabled={formLoading} data-testid="admin-reset-pw-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-red-600">Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteTarget?.type} <strong>{deleteTarget?.name}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
            <Button onClick={submitDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-full" disabled={formLoading} data-testid="admin-confirm-delete-btn">
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Church Form Dialog */}
      <Dialog open={churchFormOpen} onOpenChange={setChurchFormOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">{editingChurch ? "Edit Church" : "Add Church"}</DialogTitle>
            <DialogDescription>Enter church details</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitChurchForm} className="space-y-4">
            <div className="space-y-2">
              <Label>Church Name *</Label>
              <Input value={churchForm.name} onChange={(e) => setChurchForm({ ...churchForm, name: e.target.value })} required data-testid="admin-church-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Address / Location</Label>
              <Input value={churchForm.address} onChange={(e) => setChurchForm({ ...churchForm, address: e.target.value })} placeholder="e.g. 123 Main St" data-testid="admin-church-address-input" />
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-sage hover:bg-sage-dark text-white rounded-full" disabled={formLoading} data-testid="admin-save-church-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingChurch ? "Save" : "Add Church")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reassign Owner Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Reassign Group Owner</DialogTitle>
            <DialogDescription>Transfer ownership of <strong>{reassignGroup?.name}</strong></DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReassign} className="space-y-4">
            <div className="space-y-2">
              <Label>New Owner</Label>
              <Select value={reassignOwnerId || "none"} onValueChange={(v) => setReassignOwnerId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="admin-reassign-owner-select">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>Select a user</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-sage hover:bg-sage-dark text-white rounded-full" disabled={formLoading || !reassignOwnerId} data-testid="admin-reassign-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transfer Ownership"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editGroupOpen} onOpenChange={setEditGroupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Group</DialogTitle>
            <DialogDescription>Update group details for {selectedGroup?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEditGroup} className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input value={editGroupForm.name} onChange={(e) => setEditGroupForm({ ...editGroupForm, name: e.target.value })} required data-testid="admin-edit-group-name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={editGroupForm.description} onChange={(e) => setEditGroupForm({ ...editGroupForm, description: e.target.value })} placeholder="Brief description" data-testid="admin-edit-group-desc" />
            </div>
            <div className="space-y-2">
              <Label>Church</Label>
              <Select value={editGroupForm.church_id || "none"} onValueChange={(v) => setEditGroupForm({ ...editGroupForm, church_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="admin-edit-group-church">
                  <SelectValue placeholder="No church association" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No church association</SelectItem>
                  {churches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-sage hover:bg-sage-dark text-white rounded-full" disabled={formLoading || !editGroupForm.name.trim()} data-testid="admin-save-group-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move Members Dialog */}
      <Dialog open={moveMembersOpen} onOpenChange={setMoveMembersOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Move Members</DialogTitle>
            <DialogDescription>Select members from {moveMembersGroup?.name} and choose a target group</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Target Group *</Label>
              <Select value={moveMembersTarget || "none"} onValueChange={(v) => setMoveMembersTarget(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="move-members-target">
                  <SelectValue placeholder="Select target group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a group...</SelectItem>
                  {groups.filter((g) => g.id !== moveMembersGroup?.id && !g.is_disabled).map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_count} members)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Members ({moveMembersSelected.length}/{moveMembersData.length})</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-sage"
                  onClick={() => {
                    if (moveMembersSelected.length === moveMembersData.length) {
                      setMoveMembersSelected([]);
                    } else {
                      setMoveMembersSelected(moveMembersData.map((m) => m.id));
                    }
                  }}
                  data-testid="move-members-toggle-all"
                >
                  {moveMembersSelected.length === moveMembersData.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="max-h-60 overflow-y-auto border border-stone rounded-lg divide-y divide-stone">
                {moveMembersData.length === 0 ? (
                  <p className="p-4 text-sm text-stone-dark/50 text-center">No members in this group</p>
                ) : (
                  moveMembersData.map((member) => (
                    <label
                      key={member.id}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-stone/30 cursor-pointer"
                      data-testid={`move-member-${member.id}`}
                    >
                      <Checkbox
                        checked={moveMembersSelected.includes(member.id)}
                        onCheckedChange={() => toggleMoveMember(member.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-dark truncate">{member.name}</p>
                        <p className="text-xs text-stone-dark/50 truncate">{member.email}</p>
                      </div>
                      {member.role === "owner" && (
                        <span className="text-[10px] bg-gold/20 text-gold-dark px-1.5 py-0.5 rounded-full shrink-0">Owner</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button type="button" variant="outline" className="rounded-full">Cancel</Button></DialogClose>
            <Button
              className="bg-sage hover:bg-sage-dark text-white rounded-full"
              disabled={formLoading || !moveMembersTarget || moveMembersSelected.length === 0}
              onClick={submitMoveMembers}
              data-testid="submit-move-members-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Move ${moveMembersSelected.length} Member(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Church Admins dialog */}
      <Dialog open={!!manageAdminsChurch} onOpenChange={(o) => !o && setManageAdminsChurch(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="manage-admins-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-sage" />
              Manage admins · {manageAdminsChurch?.name}
            </DialogTitle>
            <DialogDescription>
              Church Admins can edit this church and manage its groups. Global admins can delete it.
            </DialogDescription>
          </DialogHeader>
          {manageAdminsChurch && (() => {
            const currentAdmins = users.filter(u => u.church_admin_ids?.includes(manageAdminsChurch.id));
            const q = manageAdminsSearch.toLowerCase().trim();
            const candidates = users
              .filter(u => !u.is_admin && !u.church_admin_ids?.includes(manageAdminsChurch.id))
              .filter(u => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
              .slice(0, 50);
            return (
              <div className="space-y-4">
                {/* Current admins */}
                <div>
                  <p className="text-sm font-medium text-stone-dark mb-2">
                    Current admins ({currentAdmins.length})
                  </p>
                  {currentAdmins.length === 0 ? (
                    <p className="text-sm text-stone-dark/40 italic">No admins yet — add one from the list below.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="manage-admins-current">
                      {currentAdmins.map(a => (
                        <div key={a.id} className="flex items-center justify-between bg-sage/5 rounded-lg px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-dark truncate">{a.name}</p>
                            <p className="text-xs text-stone-dark/50 truncate">{a.email}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0"
                            onClick={() => toggleChurchAdmin(a.id, manageAdminsChurch.id, false)}
                            data-testid={`manage-admins-remove-${a.id}`}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add new admin */}
                <div className="pt-3 border-t border-stone">
                  <p className="text-sm font-medium text-stone-dark mb-2">Add a new admin</p>
                  <Input
                    placeholder="Search by name or email..."
                    value={manageAdminsSearch}
                    onChange={(e) => setManageAdminsSearch(e.target.value)}
                    className="mb-2"
                    data-testid="manage-admins-search"
                  />
                  <div className="max-h-40 overflow-y-auto space-y-1" data-testid="manage-admins-candidates">
                    {candidates.length === 0 ? (
                      <p className="text-sm text-stone-dark/40 italic px-2 py-3">
                        {q ? "No users match your search." : "Start typing to find a user..."}
                      </p>
                    ) : (
                      candidates.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleChurchAdmin(u.id, manageAdminsChurch.id, true)}
                          className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-sage/5 transition-colors text-left"
                          data-testid={`manage-admins-add-${u.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-dark truncate">{u.name}</p>
                            <p className="text-xs text-stone-dark/50 truncate">{u.email}</p>
                          </div>
                          <span className="text-xs text-sage font-medium shrink-0">Add →</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageAdminsChurch(null)} className="rounded-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}

function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-dark/40" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-10 pr-10 bg-white"
        data-testid={testId}
      />
      {value && (
        <button onClick={() => onChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/40 hover:text-stone-dark">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
